import { useState, useEffect, useCallback } from "react";
import {
    Grid, Typography, TextField, Button, Alert, Snackbar,
    List, ListItemButton, ListItemText, Chip, Box, Divider,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from "@mui/material";
import { Container } from "@mui/system";
import LoadingButton from "@mui/lab/LoadingButton";
import AddIcon from '@mui/icons-material/Add';
import RTE from '../components/course_edit/rich_text_editor';
import globals from "../globals";
import { useAuth } from "../context/AuthContext";

const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('bg-BG', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const buttonSx = {
    backgroundColor: globals.colors.lightRed,
    textTransform: 'none',
    '&:hover': { backgroundColor: globals.colors.darkRed },
};

const Email = () => {
    const { token, user } = useAuth();

    const [templates, setTemplates] = useState([]);
    const [loadingTemplates, setLoadingTemplates] = useState(true);
    const [emailId, setEmailId] = useState(null);
    const [isReadOnly, setIsReadOnly] = useState(false);
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [rteKey, setRteKey] = useState(0);
    const [avatar, setAvatar] = useState(null);
    const [avatarFile, setAvatarFile] = useState(null);
    const [existingBanner, setExistingBanner] = useState(null);
    const [saving, setSaving] = useState(false);
    const [sending, setSending] = useState(false);
    const [sendingTest, setSendingTest] = useState(false);
    const [sendDialogOpen, setSendDialogOpen] = useState(false);
    const [subscriberCount, setSubscriberCount] = useState(null);
    const [loadingSubscriberCount, setLoadingSubscriberCount] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const fetchTemplates = useCallback(() => {
        setLoadingTemplates(true);
        fetch(`${globals.apiUrl}/marketing/emails`, {
            headers: { 'Authorization': `Bearer ${token}` },
        })
            .then((response) => response.json())
            .then((data) => {
                setTemplates(Array.isArray(data) ? data : []);
                setLoadingTemplates(false);
            })
            .catch(() => setLoadingTemplates(false));
    }, [token]);

    useEffect(() => {
        fetchTemplates();
    }, [fetchTemplates]);

    const resetForm = () => {
        setEmailId(null);
        setIsReadOnly(false);
        setSubject('');
        setBody('');
        setAvatar(null);
        setAvatarFile(null);
        setExistingBanner(null);
        setRteKey((k) => k + 1);
    };

    const loadTemplate = async (id) => {
        try {
            const response = await fetch(`${globals.apiUrl}/marketing/emails/${id}`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.detail || 'Грешка при зареждане на шаблона.');
            }

            setEmailId(data.id);
            setIsReadOnly(!data.is_draft);
            setSubject(data.subject);
            setBody(data.body);
            setExistingBanner(data.banner);
            setAvatar(data.banner || null);
            setAvatarFile(null);
            setRteKey((k) => k + 1);
        } catch (error) {
            setSnackbar({ open: true, message: error.message, severity: 'error' });
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setAvatar(URL.createObjectURL(file));
            setAvatarFile(file);
        }
    };

    const buildFormData = () => {
        const formData = new FormData();
        formData.append('subject', subject);
        formData.append('body', body);
        if (avatarFile) {
            formData.append('banner', avatarFile);
        } else if (existingBanner) {
            formData.append('existing_banner', existingBanner);
        }
        if (emailId) {
            formData.append('email_id', emailId);
        }
        return formData;
    };

    const validateForm = () => {
        if (!subject.trim()) {
            setSnackbar({ open: true, message: 'Моля, въведете тема на имейла.', severity: 'error' });
            return false;
        }
        if (!body.trim() || body === '<p></p>') {
            setSnackbar({ open: true, message: 'Моля, въведете съдържание на имейла.', severity: 'error' });
            return false;
        }
        if (!(avatar || existingBanner)) {
            setSnackbar({ open: true, message: 'Добавете изображение', severity: 'error' });
            return false;
        }
        return true;
    };

    const saveTemplate = async () => {
        if (!validateForm()) return;
        if (isReadOnly) return;

        setSaving(true);
        try {
            const url = emailId
                ? `${globals.apiUrl}/marketing/emails/${emailId}`
                : `${globals.apiUrl}/marketing/emails`;
            const method = emailId ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: { 'Authorization': `Bearer ${token}` },
                body: buildFormData(),
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.detail || 'Грешка при запазване.');
            }

            setEmailId(data.id);
            setExistingBanner(data.banner);
            setAvatarFile(null);
            fetchTemplates();
            setSnackbar({ open: true, message: 'Шаблонът е запазен.', severity: 'success' });
        } catch (error) {
            setSnackbar({ open: true, message: error.message, severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const sendEmail = async (endpoint, setLoading) => {
        if (!validateForm()) return;

        setLoading(true);
        try {
            const response = await fetch(`${globals.apiUrl}/marketing/${endpoint}`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: buildFormData(),
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.detail || 'Възникна грешка при изпращането.');
            }

            const message = endpoint === 'test'
                ? `Тестовият имейл е изпратен до ${user?.email}.`
                : `Изпращането е стартирано до ${data.recipients_count} абонати.`;

            if (endpoint === 'send') {
                setIsReadOnly(true);
            }

            setEmailId(data.email_id);
            fetchTemplates();
            setSnackbar({ open: true, message, severity: 'success' });
        } catch (error) {
            setSnackbar({ open: true, message: error.message, severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleOpenSendDialog = async () => {
        if (!validateForm()) return;

        setLoadingSubscriberCount(true);
        setSendDialogOpen(true);
        try {
            const response = await fetch(`${globals.apiUrl}/marketing/subscribers/count`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.detail || 'Грешка при зареждане на абонатите.');
            }
            setSubscriberCount(data.count);
        } catch (error) {
            setSendDialogOpen(false);
            setSnackbar({ open: true, message: error.message, severity: 'error' });
        } finally {
            setLoadingSubscriberCount(false);
        }
    };

    const handleCloseSendDialog = () => {
        setSendDialogOpen(false);
        setSubscriberCount(null);
    };

    const handleConfirmSend = () => {
        handleCloseSendDialog();
        sendEmail('send', setSending);
    };

    return (
        <Container>
            <Grid container>
                <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography sx={{ fontWeight: 'bold', fontSize: 20 }}>
                            {emailId ? (isReadOnly ? 'Преглед на шаблон' : 'Редактиране на шаблон') : 'Напиши имейл'}
                        </Typography>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={resetForm}
                            sx={buttonSx}
                        >
                            Нов
                        </Button>
                    </Box>
                    {isReadOnly && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Този шаблон вече е изпратен до абонатите и не може да бъде редактиран.
                        </Alert>
                    )}
                </Grid>

                <Grid item sm={12}>
                    <Typography sx={{ fontWeight: 'bold', fontSize: 15 }}>Тема</Typography>
                    <TextField
                        fullWidth
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        disabled={isReadOnly}
                    />
                </Grid>

                <Grid item>
                    <Grid container spacing={2}>
                        <Grid item sx={{ mt: 3 }}>
                            {!isReadOnly && (
                                <Button variant='contained' component='label' size='small' sx={buttonSx}>
                                    Качи изображение
                                    <input
                                        type="file"
                                        accept="image/*"
                                        hidden
                                        onChange={handleImageChange}
                                    />
                                </Button>
                            )}
                            {avatarFile && (
                                <Typography sx={{ mt: 1, fontSize: 14, color: globals.colors.textColor }}>
                                    {avatarFile.name}
                                </Typography>
                            )}
                            {avatar && (
                                <img
                                    src={avatar}
                                    alt="Banner preview"
                                    style={{ display: 'block', marginTop: 12, maxWidth: 400, maxHeight: 200, borderRadius: 8 }}
                                />
                            )}
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item sm={12} sx={{ mt: 3 }}>
                    {isReadOnly ? (
                        <Box
                            sx={{
                                border: '2px solid #e0e0e0',
                                borderRadius: '8px',
                                padding: '12px',
                                minHeight: '400px',
                            }}
                            dangerouslySetInnerHTML={{ __html: body }}
                        />
                    ) : (
                        <RTE
                            key={rteKey}
                            idName="marketing-email-body"
                            defaultValue={body}
                            onChange={(e) => setBody(e.target.value)}
                        />
                    )}
                </Grid>

                <Grid item container spacing={2} sx={{ mt: 3 }}>
                    {!isReadOnly && (
                        <Grid item>
                            <LoadingButton
                                variant='contained'
                                size='small'
                                loading={saving}
                                onClick={saveTemplate}
                                color="success"
                                sx={{
                                    textTransform: 'none',}}
                            >
                                Запази
                            </LoadingButton>
                        </Grid>
                    )}
                    <Grid item>
                        <LoadingButton
                            variant='contained'
                            size='small'
                            loading={sendingTest}
                            onClick={() => sendEmail('test', setSendingTest)}
                            sx={{backgroundColor: globals.colors.lightBlue,
                                textTransform: 'none',
                                '&:hover': { backgroundColor: globals.colors.darkBlue }}}
                        >
                            Изпрати тестов имейл
                        </LoadingButton>
                    </Grid>
                    <Grid item>
                        <LoadingButton
                            variant='contained'
                            size='small'
                            loading={sending}
                            disabled={isReadOnly}
                            onClick={handleOpenSendDialog}
                            sx={buttonSx}
                        >
                            Изпрати до абонатите
                        </LoadingButton>
                    </Grid>
                </Grid>

                <Grid item xs={12} sx={{ mt: 5 }}>
                    <Typography sx={{ fontWeight: 'bold', fontSize: 18, mb: 2 }}>
                        Запазени шаблони
                    </Typography>

                    {loadingTemplates ? (
                        <Typography sx={{ color: globals.colors.gray }}>Зареждане...</Typography>
                    ) : templates.length === 0 ? (
                        <Typography sx={{ color: globals.colors.gray }}>Няма запазени шаблони.</Typography>
                    ) : (
                        <List sx={{ bgcolor: '#fafafa', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                            {templates.map((template, index) => (
                                <Box key={template.id}>
                                    {index > 0 && <Divider />}
                                    <ListItemButton
                                        selected={emailId === template.id}
                                        onClick={() => loadTemplate(template.id)}
                                    >
                                        <ListItemText
                                            primary={template.subject}
                                            secondary={formatDate(template.created_at)}
                                        />
                                        <Chip
                                            label={template.is_draft ? 'Чернова' : 'Изпратен'}
                                            size="small"
                                            color={template.is_draft ? 'default' : 'success'}
                                            sx={{ ml: 1, flexShrink: 0 }}
                                        />
                                    </ListItemButton>
                                </Box>
                            ))}
                        </List>
                    )}
                </Grid>
            </Grid>

            <Dialog open={sendDialogOpen} onClose={handleCloseSendDialog}>
                <DialogTitle sx={{ color: globals.colors.darkBlue, fontWeight: 'bold' }}>
                    Потвърди изпращането
                </DialogTitle>
                <DialogContent>
                    {loadingSubscriberCount ? (
                        <DialogContentText>Зареждане...</DialogContentText>
                    ) : subscriberCount === 0 ? (
                        <DialogContentText>
                            Няма абонати за бюлетин. Имейлът няма да бъде изпратен.
                        </DialogContentText>
                    ) : (
                        <DialogContentText>
                            Сигурни ли сте, че искате да изпратите този имейл?
                            Ще бъде изпратен до <strong>{subscriberCount}</strong> {subscriberCount === 1 ? 'абонат' : 'абонати'}.
                        </DialogContentText>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseSendDialog} sx={{ textTransform: 'none' }}>
                        Отказ
                    </Button>
                    <Button
                        onClick={handleConfirmSend}
                        variant="contained"
                        disabled={loadingSubscriberCount || subscriberCount === 0}
                        sx={buttonSx}
                    >
                        Изпрати
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default Email;
